import React, { useState, useRef, useEffect } from 'react';
import { X, Send, User as UserIcon, Loader2, Sparkles } from 'lucide-react';
import { generateAutomationFlowChat } from '../services/geminiService';
import { AutomationFlow } from '../types';

interface AskFroukjeAutomationChatProps {
    isOpen: boolean;
    onClose: () => void;
    aiRecruiter?: {
        name: string;
        description: string;
        avatarUrl?: string;
    };
    onFlowGenerated: (flow: Partial<AutomationFlow>) => void;
    inline?: boolean;
}

interface Message {
    role: 'user' | 'model';
    text: string;
}

const AskFroukjeAutomationChat: React.FC<AskFroukjeAutomationChatProps> = ({ isOpen, onClose, aiRecruiter, onFlowGenerated, inline }) => {
    const aiName = aiRecruiter?.name || 'Froukje';
    const [messages, setMessages] = useState<Message[]>([]);

    useEffect(() => {
        setMessages(prev => prev.length === 0 ? [
            { role: 'model', text: `Hoi! Ik ben ${aiName}, jouw Automation Architect. Vertel me wat je wilt automatiseren (bijv. "Als een kandidaat een hoge match score heeft, stuur een WhatsAppje en wacht 1 dag").` }
        ] : prev);
    }, [aiName]);

    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');
        const newMessages = [...messages, { role: 'user' as const, text: userMessage }];
        setMessages(newMessages);
        setIsLoading(true);

        try {
            const history = newMessages.map(m => ({ role: m.role, content: m.text }));

            const response = await generateAutomationFlowChat(userMessage, history);

            setMessages(prev => [...prev, {
                role: 'model',
                text: response.message
            }]);

            if (response.flow) {
                onFlowGenerated(response.flow);
            }

        } catch (error) {
            setMessages(prev => [...prev, { role: 'model', text: 'Oeps, er ging iets mis bij het genereren van de flow.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) return null;

    const containerClass = inline
        ? "w-[400px] h-full bg-white flex flex-col border-l border-slate-100 z-10 shadow-[-10px_0_15px_-3px_rgba(0,0,0,0.05)] animate-slideLeft"
        : "fixed inset-y-0 right-0 w-[450px] bg-white shadow-[-10px_0_40px_rgba(0,0,0,0.1)] z-[100] flex flex-col border-l border-slate-100 animate-slideLeft";

    return (
        <div className={containerClass}>
            {/* Header */}
            <div className="h-16 border-b border-slate-100 flex items-center justify-between px-6 bg-gradient-to-r from-emerald-50 to-white">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-sm overflow-hidden shrink-0">
                        {aiRecruiter?.avatarUrl ? (
                            <img src={aiRecruiter.avatarUrl} alt="AI Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <Sparkles size={16} />
                        )}
                    </div>
                    <div>
                        <h2 className="font-bold text-slate-900 text-sm">{aiName} Builder</h2>
                        <p className="text-[10px] text-slate-500 uppercase tracking-widest font-black">Automation Architect</p>
                    </div>
                </div>
                <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                    <X size={20} />
                </button>
            </div>

            {/* Chat History */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6 custom-scrollbar bg-slate-50/50">
                {messages.map((message, index) => (
                    <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`flex max-w-[90%] gap-3 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                            {/* Avatar */}
                            <div className="flex-shrink-0 mt-1">
                                {message.role === 'model' ? (
                                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-sm overflow-hidden">
                                        {aiRecruiter?.avatarUrl ? (
                                            <img src={aiRecruiter.avatarUrl} alt="AI Avatar" className="w-full h-full object-cover" />
                                        ) : (
                                            <Sparkles size={12} />
                                        )}
                                    </div>
                                ) : (
                                    <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-white shadow-sm">
                                        <UserIcon size={12} />
                                    </div>
                                )}
                            </div>

                            {/* Message Content */}
                            <div className="flex flex-col gap-3">
                                <div className={`p-3 rounded-2xl text-sm leading-relaxed ${message.role === 'user'
                                    ? 'bg-slate-900 text-white rounded-tr-sm'
                                    : 'bg-white border border-slate-100 text-slate-700 rounded-tl-sm shadow-sm'
                                    }`}>
                                    <span dangerouslySetInnerHTML={{ __html: message.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }} />
                                </div>
                            </div>

                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex justify-start">
                        <div className="flex max-w-[80%] gap-3">
                            <div className="flex-shrink-0 mt-1">
                                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white shadow-sm overflow-hidden">
                                    {aiRecruiter?.avatarUrl ? (
                                        <img src={aiRecruiter.avatarUrl} alt="AI Avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <Sparkles size={12} />
                                    )}
                                </div>
                            </div>
                            <div className="p-3 bg-white border border-slate-100 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-2 text-sm text-slate-500">
                                <Loader2 size={14} className="animate-spin text-emerald-600" /> {aiName} bouwt de flow...
                            </div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-slate-100">
                <div className="relative flex items-center">
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Beschrijf je flow..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl py-3 pl-4 pr-12 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all resize-none h-[50px] min-h-[50px] max-h-[120px] text-sm overflow-hidden"
                        rows={1}
                        style={{ height: input ? 'auto' : '50px' }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        className="absolute right-2 p-2 bg-emerald-600 text-white rounded-lg disabled:opacity-50 disabled:bg-slate-300 hover:bg-emerald-700 transition-colors"
                    >
                        <Send size={16} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AskFroukjeAutomationChat;
